import React from "react";
import { format, parseISO } from "date-fns";
import { Link } from "react-router-dom";
import { RewardPointsLog, RewardPointsSource } from "../interface";

const SOURCE_LABELS: Record<RewardPointsSource, string> = {
    platform_fee: "Booking Platform Fee",
    alcohol: "Preferred Brand Bonus",
    admin: "HO Admin",
    subscription: "Subscription Waived",
};

const SOURCE_DOT_COLORS: Record<RewardPointsSource, string> = {
    platform_fee: "bg-blue-500",
    alcohol: "bg-primary-red",
    admin: "bg-indigo-400",
    subscription: "bg-green-500",
};

interface PointsHistoryTableProps {
    logs: RewardPointsLog[];
}

const PointsHistoryTable: React.FC<PointsHistoryTableProps> = ({ logs }) => {
    return (
        <div className="relative overflow-x-auto rounded-2xl mt-4 overflow-y-auto max-h-full bg-white shadow-sm">
            <table className="w-full text-sm text-left rtl:text-right text-gray-600">
                <thead className="text-xs text-gray-400 uppercase tracking-wide">
                    <tr>
                        <th scope="col" className="px-2 sm:px-4 py-3">Date</th>
                        <th scope="col" className="px-2 sm:px-4 py-3">Source</th>
                        <th scope="col" className="px-2 sm:px-4 py-3">Description</th>
                        <th scope="col" className="px-2 sm:px-4 py-3">Booking</th>
                        <th scope="col" className="px-2 sm:px-4 py-3">Points</th>
                        <th scope="col" className="px-2 sm:px-4 py-3">Balance</th>
                    </tr>
                </thead>
                <tbody>
                    {logs.map((log) => {
                        const isDeduction = log.reward_type === "minus";
                        const magnitude = Math.abs(log.points);
                        return (
                            <tr
                                key={log.id}
                                className="border-t border-gray-100 last:border-b-0 hover:bg-p-gray/60 transition-colors"
                            >
                                <td className="px-2 sm:px-4 py-4 min-w-28">
                                    {format(parseISO(log.created_at), "MMM d")}
                                </td>
                                <td className="px-2 sm:px-4 py-4 min-w-44">
                                    <span className="inline-flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full ${SOURCE_DOT_COLORS[log.source]}`} />
                                        {SOURCE_LABELS[log.source]}
                                    </span>
                                </td>
                                <td className="px-2 sm:px-4 py-4 min-w-44">{log.description ?? "—"}</td>
                                <td className="px-2 sm:px-4 py-4 min-w-24">
                                    {log.booking && log.booking_id ? (
                                        <Link
                                            to={`/events?tab=Bookings&id=${log.booking_id}&is_quote_booking=${log.booking.is_quote_booking}&is_upcoming_event=${!log.booking.is_past}`}
                                            className="text-primary-red underline hover:no-underline"
                                        >
                                            {log.booking.first_name} {log.booking.last_name}
                                        </Link>
                                    ) : (
                                        "—"
                                    )}
                                </td>
                                <td
                                    className={`px-2 sm:px-4 py-4 min-w-20 font-semibold ${isDeduction ? "text-primary-red" : "text-green-600"
                                        }`}
                                >
                                    {isDeduction ? `-${magnitude}` : `+${magnitude}`}
                                </td>
                                <td className="px-2 sm:px-4 py-4 min-w-24 font-semibold text-gray-800">
                                    {log.balance_after.toLocaleString()}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default PointsHistoryTable;
